// 📄 pages/Proyectos/components/CambiosSection.tsx
// "Solicitudes de cambio" (plan-proyectos-generales.md, G5). Flujo:
//   Nueva → En evaluación (el equipo estima horas, costo y días de retraso)
//   → En aprobación (el equipo elige QUIÉNES aprueban este cambio y si basta
//   con uno o tienen que aprobar todos) → Aprobada / Rechazada
//   → En implementación (tareas vinculadas a un hito) → Implementada.
// Cualquier miembro la crea, también el cliente. Nadie aprueba su propia
// solicitud. firestore.rules hace cumplir el proceso (ver cambioService.ts).
import React, { useEffect, useMemo, useState } from 'react';
import { GitPullRequest, Plus, RefreshCw, X, Save, Send, Check, CheckCircle2, Clock, History, ListTodo, Pencil } from 'lucide-react';
import { useProject } from '../../../context/ProjectContext.tsx';
import { useAuth } from '../../../context/AuthContext.tsx';
import { ProjectMember } from '../../../services/projectService.ts';
import { getHitos, Hito } from '../../../services/hitoService.ts';
import {
  Cambio, CambioEstado, CambioFormData, CambioPrioridad, ImpactoCambio, ModoAprobacionCambio, ESTADOS_CAMBIO, PRIORIDADES_CAMBIO,
  getCambios, createCambio, updateCambioDatos, updateImpacto, cambiarEstado, enviarAAprobacion, decidirCambio, aprobadoresSugeridos,
  formatoImpacto, tieneImpacto
} from '../../../services/cambioService.ts';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';
import { clasificacionDe } from '../modulosConfig.ts';
import { SOLUCIONES } from '../solucionesConfig.ts';
import { getDisplayName } from '../../../utils/displayName.ts';
import TareaFormModal from './TareaFormModal.tsx';

const ESTADO_STYLES: Record<CambioEstado, string> = {
  Nueva: 'text-gray-300 bg-white/5 border-white/15',
  'En evaluación': 'text-sky-300 bg-sky-500/10 border-sky-500/30',
  'En aprobación': 'text-amber-300 bg-amber-500/10 border-amber-500/30',
  Aprobada: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  Rechazada: 'text-red-300 bg-red-500/10 border-red-500/30',
  'En implementación': 'text-blue-300 bg-blue-500/10 border-blue-500/30',
  Implementada: 'text-emerald-200 bg-emerald-500/20 border-emerald-500/40',
  Cancelada: 'text-gray-500 bg-white/5 border-white/10'
};

const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
const labelClass = "block text-xs font-medium text-gray-400 mb-1";
const fecha = (ts: any) => (ts?.toDate ? ts.toDate().toLocaleString() : '');
const numero = (v: string) => (v.trim() === '' ? null : Number(v));

function Ventana({ titulo, onClose, children, ancho = 'max-w-lg' }: { titulo: string; onClose: () => void; children: React.ReactNode; ancho?: string }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className={`bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full ${ancho} shadow-2xl max-h-[90vh] flex flex-col`}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white truncate">{titulo}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-4">{children}</div>
      </div>
    </div>
  );
}

export default function CambiosSection() {
  const { project, projectId, esCliente } = useProject();
  const { user, profile } = useAuth();
  const [cambios, setCambios] = useState<Cambio[]>([]);
  const [hitos, setHitos] = useState<Hito[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState<CambioEstado | 'abiertas' | 'todas'>('abiertas');
  const [form, setForm] = useState<Cambio | 'nuevo' | null>(null);
  const [seleccionado, setSeleccionado] = useState<string | null>(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const [c, h] = await Promise.all([getCambios(projectId), getHitos(projectId).catch(() => [] as Hito[])]);
      setCambios(c);
      setHitos(h);
    } catch (err) {
      console.error('Error cargando solicitudes de cambio:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const visibles = useMemo(() => cambios.filter((c) => {
    if (filtro === 'todas') return true;
    if (filtro === 'abiertas') return !['Implementada', 'Rechazada', 'Cancelada'].includes(c.estado);
    return c.estado === filtro;
  }), [cambios, filtro]);

  const contar = (estados: CambioEstado[]) => cambios.filter((c) => estados.includes(c.estado)).length;
  const porAprobarMias = user ? cambios.filter((c) => c.estado === 'En aprobación' && c.aprobadores.includes(user.uid) && !c.decididoPor.includes(user.uid)).length : 0;

  if (!project || !user) return null;
  const actual = cambios.find((c) => c.id === seleccionado) || null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Por evaluar', n: contar(['Nueva', 'En evaluación']), estado: 'Nueva' as const },
          { label: 'En aprobación', n: contar(['En aprobación']), estado: 'En aprobación' as const },
          { label: 'Aprobadas / en curso', n: contar(['Aprobada', 'En implementación']), estado: 'En implementación' as const },
          { label: 'Implementadas', n: contar(['Implementada']), estado: 'Implementada' as const }
        ].map((k) => (
          <div key={k.label} className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <p className="text-xs text-gray-400">{k.label}</p>
            <p className="text-2xl font-light text-white mt-1">{k.n}</p>
          </div>
        ))}
      </div>

      {porAprobarMias > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 text-amber-200 p-3 rounded-xl text-sm flex items-center gap-2">
          <Clock className="w-4 h-4" /> Tienes {porAprobarMias} solicitud{porAprobarMias > 1 ? 'es' : ''} de cambio esperando tu aprobación.
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <select value={filtro} onChange={(e) => setFiltro(e.target.value as any)} className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none">
          <option value="abiertas">Abiertas</option>
          <option value="todas">Todas</option>
          {ESTADOS_CAMBIO.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <button onClick={() => setForm('nuevo')} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-xl text-sm font-semibold text-white">
          <Plus className="w-4 h-4" /> Nueva solicitud
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <p className="text-sm">Cargando solicitudes...</p>
        </div>
      ) : visibles.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <GitPullRequest className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">{cambios.length === 0 ? 'Todavía no hay solicitudes de cambio. Cuando el cliente o el equipo pidan algo nuevo durante el proyecto, regístralo acá.' : 'Ninguna solicitud coincide con el filtro.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibles.map((c) => (
            <button key={c.id} onClick={() => setSeleccionado(c.id)} className="w-full text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-4 transition-all">
              <div className="flex items-start justify-between gap-3">
                <h4 className="text-sm font-semibold text-white">{c.titulo}</h4>
                <span className={`flex-shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${ESTADO_STYLES[c.estado]}`}>{c.estado}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap mt-2 text-xs text-gray-500">
                <span>Prioridad {c.prioridad}</span>
                <span>· Pide {c.solicitadoPorNombre}</span>
                {c.area && <span>· {SOLUCIONES[c.area]?.label || c.area}</span>}
                {c.hitoNombre && <span className="text-blue-300/80">· 🏁 {c.hitoNombre}</span>}
                {tieneImpacto(c.impacto) && <span>· {formatoImpacto(c.impacto)}</span>}
                {c.estado === 'En aprobación' && <span className="text-amber-300">· {c.decididoPor.length}/{c.aprobadores.length} decisiones</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {form && (
        <CambioFormModal
          projectId={projectId}
          cambio={form === 'nuevo' ? null : form}
          hitos={hitos}
          clasificacion={clasificacionDe(project)}
          uid={user.uid}
          nombre={getDisplayName(user, profile)}
          onClose={() => setForm(null)}
          onSaved={() => { setForm(null); cargar(); }}
        />
      )}

      {actual && (
        <CambioDetalle
          cambio={actual}
          hitos={hitos}
          esCliente={esCliente}
          onClose={() => setSeleccionado(null)}
          onEditar={() => { setForm(actual); setSeleccionado(null); }}
          onCambio={cargar}
        />
      )}
    </div>
  );
}

function CambioFormModal({ projectId, cambio, hitos, clasificacion, uid, nombre, onClose, onSaved }: {
  projectId: string; cambio: Cambio | null; hitos: Hito[]; clasificacion: { modoArea: boolean; opciones: string[] };
  uid: string; nombre: string; onClose: () => void; onSaved: () => void;
}) {
  const [data, setData] = useState<CambioFormData>(cambio
    ? { titulo: cambio.titulo, descripcion: cambio.descripcion, motivo: cambio.motivo, prioridad: cambio.prioridad, area: cambio.area, hitoId: cambio.hitoId }
    : { titulo: '', descripcion: '', motivo: '', prioridad: 'Media', area: '', hitoId: '' });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    const hitoNombre = hitos.find((h) => h.id === data.hitoId)?.nombre || '';
    try {
      if (cambio) await updateCambioDatos(projectId, cambio.id, data, hitoNombre, uid, nombre);
      else await createCambio(projectId, data, hitoNombre, uid, nombre);
      onSaved();
    } catch (err: any) {
      setError(err.message || 'No se pudo guardar la solicitud.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Ventana titulo={cambio ? 'Editar solicitud de cambio' : 'Nueva solicitud de cambio'} onClose={onClose}>
      <form onSubmit={guardar} className="space-y-4">
        {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}
        <div>
          <label className={labelClass}>Qué se quiere cambiar*</label>
          <input autoFocus value={data.titulo} onChange={(e) => setData({ ...data, titulo: e.target.value })} maxLength={150} placeholder="ej. Agregar pago con QR en caja" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Descripción*</label>
          <textarea value={data.descripcion} onChange={(e) => setData({ ...data, descripcion: e.target.value })} rows={3} maxLength={3000} className={`${inputClass} resize-none`} placeholder="Detalle del cambio, cómo debería funcionar..." />
        </div>
        <div>
          <label className={labelClass}>Motivo</label>
          <textarea value={data.motivo} onChange={(e) => setData({ ...data, motivo: e.target.value })} rows={2} maxLength={1000} className={`${inputClass} resize-none`} placeholder="Por qué se necesita (negocio, legal, error detectado...)" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Prioridad</label>
            <select value={data.prioridad} onChange={(e) => setData({ ...data, prioridad: e.target.value as CambioPrioridad })} className={inputClass}>
              {PRIORIDADES_CAMBIO.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>{clasificacion.modoArea ? 'Área' : 'Solución'}</label>
            <select value={data.area} onChange={(e) => setData({ ...data, area: e.target.value })} className={inputClass}>
              <option value="">{clasificacion.modoArea ? 'Sin área' : 'Sin solución'}</option>
              {clasificacion.opciones.map((o) => <option key={o} value={o}>{SOLUCIONES[o]?.label || o}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Hito afectado</label>
            <select value={data.hitoId} onChange={(e) => setData({ ...data, hitoId: e.target.value })} className={inputClass}>
              <option value="">Ninguno</option>
              {hitos.map((h) => <option key={h.id} value={h.id}>{h.nombre}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5">Cancelar</button>
          <button type="submit" disabled={guardando} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-white">
            {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
          </button>
        </div>
      </form>
    </Ventana>
  );
}

function CambioDetalle({ cambio, hitos, esCliente, onClose, onEditar, onCambio }: {
  cambio: Cambio; hitos: Hito[]; esCliente: boolean; onClose: () => void; onEditar: () => void; onCambio: () => void;
}) {
  const { project, projectId } = useProject();
  const { user, profile } = useAuth();
  const [impacto, setImpacto] = useState({
    horas: cambio.impacto.horas?.toString() ?? '', costo: cambio.impacto.costo?.toString() ?? '', dias: cambio.impacto.dias?.toString() ?? '', notas: cambio.impacto.notas || ''
  });
  const [comentario, setComentario] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviarAbierto, setEnviarAbierto] = useState(false);
  // Enviar a aprobación sin estimar: se confirma (el aprobador suele decidir por horas y costo).
  const [confirmarSinImpacto, setConfirmarSinImpacto] = useState(false);
  const [crearTarea, setCrearTarea] = useState(false);

  if (!project || !user) return null;
  const nombre = getDisplayName(user, profile);
  const miembros: ProjectMember[] = (project.miembros || []).filter((m) => m.activo !== false);
  const urlProyecto = `${window.location.origin}/proyecto/${projectId}`;
  const esSolicitante = cambio.solicitadoPor === user.uid;
  const equipo = !esCliente;
  const editableDatos = (esSolicitante || equipo) && ['Nueva', 'En evaluación'].includes(cambio.estado);
  const editableImpacto = equipo && ['Nueva', 'En evaluación', 'Rechazada'].includes(cambio.estado);
  const meToca = cambio.estado === 'En aprobación' && cambio.aprobadores.includes(user.uid) && !cambio.decididoPor.includes(user.uid) && !esSolicitante;
  const emailSolicitante = miembros.find((m) => m.userId === cambio.solicitadoPor)?.email || '';

  const ejecutar = async (accion: () => Promise<unknown>) => {
    setTrabajando(true);
    setError(null);
    try {
      await accion();
      onCambio();
    } catch (err: any) {
      setError(err.message || 'No se pudo completar la acción.');
    } finally {
      setTrabajando(false);
    }
  };

  const abrirEnvio = () => (tieneImpacto(cambio.impacto) ? setEnviarAbierto(true) : setConfirmarSinImpacto(true));

  const guardarImpacto = () => ejecutar(() => updateImpacto(projectId, cambio, {
    horas: numero(impacto.horas), costo: numero(impacto.costo), dias: numero(impacto.dias), notas: impacto.notas.trim()
  } as ImpactoCambio, user.uid, nombre));

  const boton = 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-40';

  return (
    <Ventana titulo={cambio.titulo} onClose={onClose} ancho="max-w-2xl">
      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${ESTADO_STYLES[cambio.estado]}`}>{cambio.estado}</span>
        <span className="text-gray-400">Prioridad {cambio.prioridad}</span>
        <span className="text-gray-400">· Pidió {cambio.solicitadoPorNombre} el {fecha(cambio.creadoEn)}</span>
        {cambio.area && <span className="text-gray-400">· {SOLUCIONES[cambio.area]?.label || cambio.area}</span>}
        {cambio.hitoNombre && <span className="text-blue-300/80">· 🏁 {cambio.hitoNombre}</span>}
        {editableDatos && (
          <button onClick={onEditar} className="ml-auto flex items-center gap-1 text-gray-400 hover:text-white"><Pencil className="w-3.5 h-3.5" /> Editar</button>
        )}
      </div>

      <div className="space-y-2 text-sm">
        <p className="text-gray-200 whitespace-pre-wrap">{cambio.descripcion}</p>
        {cambio.motivo && <p className="text-gray-400 text-xs whitespace-pre-wrap"><span className="text-gray-500">Motivo:</span> {cambio.motivo}</p>}
      </div>

      {/* Impacto: lo estima el equipo; lo ve también el cliente. */}
      <div className="bg-black/20 border border-white/10 rounded-xl p-4 space-y-3">
        <p className="text-xs font-semibold text-gray-300">Impacto estimado</p>
        {editableImpacto ? (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div><label className={labelClass}>Horas</label><input type="number" min="0" value={impacto.horas} onChange={(e) => setImpacto({ ...impacto, horas: e.target.value })} className={inputClass} /></div>
              <div><label className={labelClass}>Costo</label><input type="number" min="0" value={impacto.costo} onChange={(e) => setImpacto({ ...impacto, costo: e.target.value })} className={inputClass} /></div>
              <div><label className={labelClass}>Días de retraso</label><input type="number" min="0" value={impacto.dias} onChange={(e) => setImpacto({ ...impacto, dias: e.target.value })} className={inputClass} /></div>
            </div>
            <textarea value={impacto.notas} onChange={(e) => setImpacto({ ...impacto, notas: e.target.value })} rows={2} maxLength={1000} placeholder="Notas de la estimación (supuestos, riesgos...)" className={`${inputClass} resize-none`} />
            <div className="flex justify-end">
              <button onClick={guardarImpacto} disabled={trabajando} className={`${boton} bg-white/10 hover:bg-white/15 text-white`}><Save className="w-3.5 h-3.5" /> Guardar estimación</button>
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-300">
            {tieneImpacto(cambio.impacto) ? formatoImpacto(cambio.impacto) : 'Sin estimar'}
            {cambio.impacto.notas && <span className="block text-xs text-gray-500 mt-1 whitespace-pre-wrap">{cambio.impacto.notas}</span>}
          </p>
        )}
      </div>

      {/* Aprobación de ESTE cambio */}
      {cambio.aprobadores.length > 0 && (
        <div className="bg-black/20 border border-white/10 rounded-xl p-4 space-y-2">
          <p className="text-xs font-semibold text-gray-300">
            Aprobadores · {cambio.modo === 'todos' && cambio.aprobadores.length > 1 ? 'tienen que aprobar todos' : 'basta con uno'}
          </p>
          <ul className="space-y-1 text-xs">
            {cambio.aprobadores.map((a) => {
              const d = cambio.decisiones.find((x) => x.uid === a);
              return (
                <li key={a} className="flex items-center justify-between gap-2">
                  <span className="text-gray-200">{cambio.aprobadoresNombres[a] || a}</span>
                  <span className={d ? (d.decision === 'aprobar' ? 'text-emerald-300' : 'text-red-300') : 'text-gray-500'}>
                    {d ? `${d.decision === 'aprobar' ? 'Aprobó' : 'Rechazó'}${d.comentario ? ` · "${d.comentario}"` : ''}` : 'Pendiente'}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {meToca && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-2">
          <p className="text-sm text-amber-200">Te asignaron aprobar este cambio.</p>
          <input value={comentario} onChange={(e) => setComentario(e.target.value)} maxLength={500} placeholder="Comentario (opcional)" className={inputClass} />
          <div className="flex justify-end gap-2">
            <button disabled={trabajando} onClick={() => ejecutar(() => decidirCambio(projectId, cambio, 'rechazar', comentario, user.uid, nombre, emailSolicitante, project.nombre, urlProyecto))} className={`${boton} text-red-400 hover:bg-red-500/10`}>
              <X className="w-3.5 h-3.5" /> Rechazar
            </button>
            <button disabled={trabajando} onClick={() => ejecutar(() => decidirCambio(projectId, cambio, 'aprobar', comentario, user.uid, nombre, emailSolicitante, project.nombre, urlProyecto))} className={`${boton} bg-emerald-600 hover:bg-emerald-500 text-[#fff]`}>
              <Check className="w-3.5 h-3.5" /> Aprobar
            </button>
          </div>
        </div>
      )}

      {/* Acciones del equipo según el estado */}
      {equipo && (
        <div className="flex flex-wrap justify-end gap-2">
          {['Nueva', 'En evaluación', 'Rechazada'].includes(cambio.estado) && (
            <button disabled={trabajando} onClick={abrirEnvio} className={`${boton} bg-blue-600 hover:bg-blue-500 text-[#fff]`}><Send className="w-3.5 h-3.5" /> Enviar a aprobación</button>
          )}
          {cambio.estado === 'En aprobación' && (
            <button disabled={trabajando} onClick={() => setEnviarAbierto(true)} className={`${boton} bg-white/10 hover:bg-white/15 text-white`}>Cambiar aprobadores</button>
          )}
          {cambio.estado === 'Aprobada' && (
            <button disabled={trabajando} onClick={() => ejecutar(() => cambiarEstado(projectId, cambio.id, 'En implementación', user.uid, nombre))} className={`${boton} bg-blue-600 hover:bg-blue-500 text-[#fff]`}>Iniciar implementación</button>
          )}
          {['Aprobada', 'En implementación'].includes(cambio.estado) && (
            <button disabled={trabajando} onClick={() => setCrearTarea(true)} className={`${boton} bg-white/10 hover:bg-white/15 text-white`}><ListTodo className="w-3.5 h-3.5" /> Crear tarea</button>
          )}
          {cambio.estado === 'En implementación' && (
            <button disabled={trabajando} onClick={() => ejecutar(() => cambiarEstado(projectId, cambio.id, 'Implementada', user.uid, nombre))} className={`${boton} bg-emerald-600 hover:bg-emerald-500 text-[#fff]`}><CheckCircle2 className="w-3.5 h-3.5" /> Marcar implementada</button>
          )}
          {!['Implementada', 'Cancelada'].includes(cambio.estado) && (
            <button disabled={trabajando} onClick={() => ejecutar(() => cambiarEstado(projectId, cambio.id, 'Cancelada', user.uid, nombre))} className={`${boton} text-gray-400 hover:text-white hover:bg-white/5`}>Cancelar solicitud</button>
          )}
        </div>
      )}

      {/* Historial */}
      <div className="border-t border-white/10 pt-3">
        <p className="text-xs font-semibold text-gray-400 flex items-center gap-1.5 mb-2"><History className="w-3.5 h-3.5" /> Historial</p>
        <ul className="space-y-1 text-xs">
          {[...cambio.historial].reverse().map((e, i) => (
            <li key={i} className="text-gray-400">
              <span className="text-gray-200">{e.actorNombre}</span> {e.tipo}{e.detalle ? ` · ${e.detalle}` : ''} <span className="text-gray-600">· {fecha(e.en)}</span>
            </li>
          ))}
        </ul>
      </div>

      {confirmarSinImpacto && (
        <ConfirmDialog
          title="¿Enviar sin impacto estimado?"
          message="Este cambio no tiene horas, costo ni días de retraso estimados. Los aprobadores suelen decidir con esos datos."
          confirmLabel="Enviar igual"
          cancelLabel="Estimar primero"
          onConfirm={() => { setConfirmarSinImpacto(false); setEnviarAbierto(true); }}
          onCancel={() => setConfirmarSinImpacto(false)}
        />
      )}

      {enviarAbierto && (
        <EnviarAprobacion
          cambio={cambio}
          miembros={miembros}
          creadoPor={project.creadoPor}
          onClose={() => setEnviarAbierto(false)}
          onEnviar={(aprobadores, modo) => ejecutar(async () => {
            await enviarAAprobacion(projectId, cambio, aprobadores, modo, miembros, user.uid, nombre, project.nombre, urlProyecto);
            setEnviarAbierto(false);
          })}
        />
      )}

      {crearTarea && (
        <TareaFormModal
          projectId={projectId}
          proyectoNombre={project.nombre}
          miembros={miembros}
          creadoPor={user.uid}
          creadoPorNombre={nombre}
          hitos={hitos}
          hitoInicial={cambio.hitoId}
          tituloInicial={`Cambio: ${cambio.titulo}`}
          onClose={() => setCrearTarea(false)}
          onSaved={() => setCrearTarea(false)}
        />
      )}
    </Ventana>
  );
}

/** El equipo elige quiénes aprueban ESTE cambio (sugeridos: creador del proyecto y cliente). */
function EnviarAprobacion({ cambio, miembros, creadoPor, onClose, onEnviar }: {
  cambio: Cambio; miembros: ProjectMember[]; creadoPor: string; onClose: () => void;
  onEnviar: (aprobadores: string[], modo: ModoAprobacionCambio) => void;
}) {
  const elegibles = miembros.filter((m) => m.userId !== cambio.solicitadoPor);
  const [seleccion, setSeleccion] = useState<string[]>(
    cambio.aprobadores.length ? cambio.aprobadores : aprobadoresSugeridos(creadoPor, miembros, cambio.solicitadoPor)
  );
  const [modo, setModo] = useState<ModoAprobacionCambio>(cambio.modo || 'uno');
  const toggle = (uid: string) => setSeleccion((p) => (p.includes(uid) ? p.filter((x) => x !== uid) : [...p, uid]));

  return (
    <Ventana titulo="Enviar a aprobación" onClose={onClose}>
      <p className="text-xs text-gray-400">
        Elige quiénes deben aprobar este cambio. Les llega un correo. Quien lo pidió ({cambio.solicitadoPorNombre}) no puede aprobarlo.
        {cambio.decisiones.length > 0 && ' Las decisiones anteriores se reinician.'}
      </p>
      <div className="space-y-1 max-h-60 overflow-y-auto">
        {elegibles.map((m) => (
          <label key={m.userId} className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer hover:text-white">
            <input type="checkbox" checked={seleccion.includes(m.userId)} onChange={() => toggle(m.userId)} className="w-4 h-4 accent-blue-500" />
            <span className="truncate">{m.email}</span>
            {m.rol === 'cliente' && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">Cliente</span>}
            {m.userId === creadoPor && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300">Creador del proyecto</span>}
          </label>
        ))}
      </div>
      {seleccion.length > 1 && (
        <div className="flex flex-wrap gap-4 text-xs text-gray-300">
          <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={modo === 'uno'} onChange={() => setModo('uno')} className="accent-blue-500" /> Basta con que apruebe uno</label>
          <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={modo === 'todos'} onChange={() => setModo('todos')} className="accent-blue-500" /> Tienen que aprobar todos</label>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5">Cancelar</button>
        <button disabled={!seleccion.length} onClick={() => onEnviar(seleccion, modo)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 px-4 py-2 rounded-lg text-sm font-semibold text-white">
          <Send className="w-4 h-4" /> Enviar
        </button>
      </div>
    </Ventana>
  );
}
